import { useState, useEffect } from "react"; // No useCallback/useMemo needed!
import BlogCard from "./BlogCard";
import RelatedArticleCard from "./RelatedArticleCard";
import relatedArticles from "../../data/relatedArticles.json";
import { getBlogCategories, getBlogList } from "../../helper/backend-methods";

const BlogListing = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(""); // 👈 Add this
  const [sortOption, setSortOption] = useState("Latest");
  const [blogs, setBlogs] = useState([]);
  const [mustReadBlogs, setMustReadBlogs] = useState([]);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const blogsPerPage = 4;

  /* 🔹 PAGINATION CALCULATIONS */
  const totalPages = Math.ceil(totalBlogs / blogsPerPage);

  /* 🔹 PAGINATION FUNCTIONS */
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  /* 🔹 GENERATE PAGE NUMBERS */
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 4;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 1);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 2) {
        endPage = Math.min(maxVisiblePages, totalPages);
      }

      if (currentPage >= totalPages - 1) {
        startPage = Math.max(1, totalPages - 3);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  /* 🔹 DEBOUNCE SEARCH INPUT */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  /* 🔹 HANDLE SEARCH */
  const handleSearch = (e) => {
    setSearchQuery(e.target.value); // Instant input update
  };

  /* 🔹 HANDLE SORT */
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  /* 🔹 FETCH CATEGORIES ON MOUNT */
  useEffect(() => {
    getBlogCategories()
      .then(({ data }) => {
        if (data.Status) {
          setCategories(data.Data);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, []);

  const getBlogs = () => {
    setLoading(true);
    getBlogList({
      Category: activeCategory === "All" ? null : activeCategory,
      PageIndex: currentPage - 1,
      PageSize: blogsPerPage,
      SearchTerm: debouncedQuery || null,
      Sorting: sortOption
    })
      .then(({ data }) => {
        if (data.Status) {
          setBlogs(data.Data?.Blogs || []);
          setTotalBlogs(data.Data?.TotalCount || 0);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error(e);
        setBlogs([]);
        setTotalBlogs(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };


  const getMustReadBlogs = () => {
    getBlogList({
      Category: null,
      PageIndex: 0,
      PageSize: 3,
      SortBy: "oldest",
      IsMustReadBlog: true
    })
    .then(({ data }) => {
      if (data.Status) {
        setMustReadBlogs(data.Data?.Blogs || []);
      }
    })
    .catch((e) => {
      console.error(e);
    })
  };
  /* 🔹 FETCH BLOGS - directly in useEffect */
  useEffect(() => {
    getBlogs();
  }, [activeCategory, currentPage, debouncedQuery, sortOption]);

  /* 🔹 RESET PAGE WHEN FILTERS CHANGE */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, debouncedQuery, sortOption]); // 👈 Use debounced

  useEffect(() => {
    getMustReadBlogs();
  }, []);

  return (
    <section id="blog-listing">
      <div className="wrapper">
        <h1 className="main-hd">LATEST NEWS AND ARTICLES</h1>

        {/* Search & Sort */}
        <div className="top-controls">
          <div className="position-relative">
            <input type="text" placeholder="Search by keywords..." value={searchQuery} onChange={handleSearch} />
            <span className="search-icon">
              <svg
                className="menu-search-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="#AEAEAE"
              >
                <path d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"></path>
              </svg>
            </span>
          </div>
          <select value={sortOption} onChange={handleSortChange}>
            <option value="Latest">Sort by: Latest</option>
            <option value="oldest">Sort by: Oldest</option>
          </select>
        </div>

        <div className="content-wrapper">
          {/* RIGHT SIDE */}
          <main className="blog-list">
            {/* CATEGORY TABS (FROM JSON) */}
            
            <div className="categories">
                <button className={activeCategory === "All" ? "active" : ""} onClick={() => setActiveCategory("All")}>
                  All
                </button>

              {categories.map((category) => (
                <button key={category.Name} className={activeCategory === category.Name ? "active" : ""} onClick={() => setActiveCategory(category.Name)}>
                  {category.Name}
                </button>
              ))}
            </div>

            {/* BLOG LIST */}
            <div id="blogList" className="blog-grid">
              {blogs.length > 0 ? (
                blogs.map((blog) => <BlogCard key={blog.Id} blog={blog} type="list" />)
              ) : (
                <div className="no-results">
                  <p>No blogs found. Try a different search or category.</p>
                </div>
              )}
            </div>

            {/* PAGINATION */}
            {totalBlogs > 0 && totalPages > 1 && (
              <div className="blog-pagination">
                <button
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className="pagination-btn first-last"
                  aria-label="Go to first page"
                >
                  &lt;&lt;
                </button>

                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="pagination-btn prev-next"
                  aria-label="Go to previous page"
                >
                  &lt;
                </button>

                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`pagination-btn page-number ${currentPage === page ? "active" : ""}`}
                    aria-label={`Go to page ${page}`}
                    aria-current={currentPage === page ? "page" : undefined}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="pagination-btn prev-next"
                  aria-label="Go to next page"
                >
                  &gt;
                </button>

                <button
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className="pagination-btn first-last"
                  aria-label="Go to last page"
                >
                  &gt;&gt;
                </button>
              </div>
            )}

            {/* RESULTS COUNT */}
            {/* <div className="results-count">
              Showing {Math.min(startIndex + 1, totalBlogs)}-{Math.min(endIndex, totalBlogs)} of {totalBlogs} results
            </div> */}
          </main>
          {/* LEFT SIDE */}
          <aside className="related-articles">
            <h3 className="realted-heading">Must Read Articles</h3>
            {mustReadBlogs.map((article) => (
              <RelatedArticleCard key={article.id} article={article} />
            ))}
          </aside>
        </div>
      </div>
    </section>
  );
};

export default BlogListing;
